import { useState } from "react";
import { Link } from "react-router-dom";
import GlobalNav from "../components/GlobalNav";

const G = "#004D35";
const MINT = "#a8f0c6";
const BG = "#f5f3ef";
const BORDER = "#e8e5e0";
const TODAY = "March 8, 2026";

function LegalFooter() {
  return (
    <footer style={{ background: "#0a1a0f", padding: "32px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
      <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
        <svg width={28} height={28} viewBox="0 0 40 40" fill="none">
          <rect width="40" height="40" rx="10" fill={G} />
          <path d="M10 11 L20 28 L30 11" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="20" cy="28" r="2.5" fill={MINT} />
        </svg>
        <span style={{ fontSize: 18, fontWeight: 700, color: "#fff", fontFamily: "Georgia, serif", letterSpacing: "-0.02em" }}>Viysr</span>
      </Link>
      <div style={{ display: "flex", gap: 28 }}>
        {[["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Contact", "/contact"]].map(([label, to]) => (
          <Link key={to} to={to} style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, fontFamily: "sans-serif", textDecoration: "none" }}
            onMouseEnter={e => e.target.style.color = "rgba(255,255,255,0.75)"}
            onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.4)"}
          >{label}</Link>
        ))}
      </div>
      <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 13, fontFamily: "sans-serif", margin: 0 }}>© 2026 Viysr, Inc. All rights reserved.</p>
    </footer>
  );
}

function LegalShell({ title, lastUpdated, children }) {
  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "60px 48px 100px" }}>
      <p style={{ fontSize: 11, fontFamily: "sans-serif", letterSpacing: "0.16em", color: G, fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>◆ LEGAL</p>
      <h1 style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 400, color: "#0a1a0f", margin: "0 0 8px", fontFamily: "Georgia, serif", lineHeight: 1.1 }}>{title}</h1>
      <p style={{ fontSize: 13, color: "#bbb", fontFamily: "sans-serif", margin: "0 0 48px" }}>Last updated: {lastUpdated}</p>
      <div style={{ fontSize: 15, fontFamily: "sans-serif", color: "#444", lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif", margin: "0 0 12px" }}>{title}</h2>
      {children}
    </div>
  );
}

function P({ children }) {
  return <p style={{ margin: "0 0 14px", lineHeight: 1.8 }}>{children}</p>;
}

function PageWrapper({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: BG }}>
      <GlobalNav page="browse" />
      {children}
      <LegalFooter />
    </div>
  );
}

export function PrivacyPage() {
  return (
    <PageWrapper>
      <LegalShell title="Privacy Policy" lastUpdated={TODAY}>
        <P>Viysr ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. Please read this policy carefully. If you disagree with its terms, please discontinue use of the platform.</P>

        <Section title="1. Information We Collect">
          <P><strong>Information you provide directly:</strong> When you create an account, we collect your name, email address, password, and business information. Advisors additionally provide professional credentials, specialty information, and billing details.</P>
          <P><strong>Business profile information:</strong> To help match you with advisors, we collect information about your business including industry, size, and advisory needs. This information is used solely to improve your experience on Viysr.</P>
          <P><strong>Communications:</strong> If you contact us or communicate with advisors through our platform, we may retain those communications.</P>
          <P><strong>Usage data:</strong> We automatically collect information about how you interact with our platform, including pages visited, features used, and time spent.</P>
          <P><strong>Marketing preferences:</strong> With your explicit consent at registration, we may collect your preference to receive marketing communications from Viysr.</P>
        </Section>

        <Section title="2. How We Use Your Information">
          <P>We use the information we collect to: operate and improve the Viysr platform; match business owners with relevant advisors; process transactions and send related information; send administrative communications; send marketing communications where you have opted in; comply with legal obligations; and prevent fraudulent or harmful activity.</P>
          <P>We do not sell your personal information to third parties. We do not allow advertisers to target you based on your personal data.</P>
        </Section>

        <Section title="3. How We Share Your Information">
          <P><strong>With advisors:</strong> When you initiate contact with an advisor, your name and message are shared with that advisor. Your full profile is not shared without your action.</P>
          <P><strong>Service providers:</strong> We share data with trusted third-party service providers who assist us in operating the platform (e.g., payment processing via Stripe, email delivery, hosting). These providers are contractually obligated to protect your data.</P>
          <P><strong>Legal requirements:</strong> We may disclose your information if required by law or in response to valid legal process.</P>
          <P><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</P>
        </Section>

        <Section title="4. Data Retention">
          <P>We retain your personal information for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time through your account settings. Upon deletion, we will remove your personal information within 30 days, except where we are required to retain it for legal or legitimate business purposes.</P>
        </Section>

        <Section title="5. Your Rights">
          <P>Depending on your location, you may have the right to: access the personal information we hold about you; correct inaccurate data; request deletion of your data; object to or restrict our processing of your data; withdraw consent for marketing communications at any time; and data portability.</P>
          <P>To exercise any of these rights, contact us at <strong>privacy@viysr.com</strong>.</P>
        </Section>

        <Section title="6. Cookies">
          <P>We use cookies and similar tracking technologies to operate our platform, remember your preferences, and analyze usage. You can control cookies through your browser settings, though disabling certain cookies may affect platform functionality.</P>
        </Section>

        <Section title="7. Security">
          <P>We implement commercially reasonable technical and organizational measures to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</P>
        </Section>

        <Section title="8. Children's Privacy">
          <P>Viysr is not directed at individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that a minor has provided us with personal information, we will delete it promptly.</P>
        </Section>

        <Section title="9. Changes to This Policy">
          <P>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the platform after changes constitutes acceptance of the updated policy.</P>
        </Section>

        <Section title="10. Contact Us">
          <P>If you have questions about this Privacy Policy, please contact us at:<br /><strong>privacy@viysr.com</strong><br />Viysr, Inc.</P>
        </Section>
      </LegalShell>
    </PageWrapper>
  );
}

export function TermsPage() {
  return (
    <PageWrapper>
      <LegalShell title="Terms of Service" lastUpdated={TODAY}>
        <P>Welcome to Viysr. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the platform.</P>

        <Section title="1. About Viysr">
          <P>Viysr is a marketplace platform that connects small and medium-sized business owners with independent professional advisors including CPAs, payroll specialists, bankers, HR consultants, insurance agents, and retirement planners. Viysr does not itself provide advisory services and is not responsible for the advice or services provided by advisors on the platform.</P>
        </Section>

        <Section title="2. Eligibility">
          <P>You must be at least 18 years old and capable of entering into a legally binding agreement to use Viysr. By creating an account, you represent that you meet these requirements.</P>
        </Section>

        <Section title="3. Accounts">
          <P>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to notify us immediately of any unauthorized use of your account.</P>
          <P>You may not create accounts for others without their consent, impersonate any person or entity, or use the platform for any unlawful purpose.</P>
        </Section>

        <Section title="4. Business Owner Terms">
          <P>Business owners may browse advisor profiles and initiate contact at no charge. Viysr does not guarantee the availability, quality, or suitability of any advisor. Any engagement between a business owner and an advisor is a separate agreement between those parties. Viysr is not a party to any advisory engagement and accepts no liability for outcomes.</P>
        </Section>

        <Section title="5. Advisor Terms">
          <P>Advisors must apply to join the Viysr network and provide accurate information about their credentials, experience, and services. Advisors agree to maintain their profile information in an accurate and up-to-date state.</P>
          <P><strong>Profile setup.</strong> The current application and profile setup flow does not require payment or enrollment in a subscription. An advisor must be approved by Viysr before publishing a profile. Publication remains subject to credential review, accurate information, and these Terms.</P>
          <P><strong>Existing paid services.</strong> Any previously agreed subscription or paid service remains subject to its existing billing terms. Changing or publishing a profile does not start a new subscription or cancel an existing one. Advisors with an existing subscription can manage it separately through their billing settings.</P>
          <P><strong>Profile visibility.</strong> Advisors may hide their profile through account settings. Viysr may also hide or suspend a profile for moderation reasons. Hiding a profile does not itself change any existing billing agreement.</P>
          <P><strong>Account deletion.</strong> Advisors may permanently delete their account through their account settings. Deletion will cancel any active subscription immediately, remove the advisor's profile from the platform, and permanently delete all associated data within 30 days. Deletion cannot be undone.</P>
          <P>Viysr reserves the right to suspend or remove any advisor from the platform for violation of these Terms, credible complaints from business owners, or failure to maintain accurate credentials. In cases of suspension by Viysr, a prorated refund for any unused portion of a prepaid billing period may be issued at Viysr's discretion.</P>
        </Section>

        <Section title="6. Prohibited Conduct">
          <P>You agree not to use the platform for illegal purposes; post false, misleading, or defamatory content; harass, threaten, or harm other users; collect personal contact information for unsolicited bulk outreach; or attempt to gain unauthorized access to the platform. Visitors may contact advisors directly by email or book through an advisor's own calendar.</P>
        </Section>

        <Section title="7. Intellectual Property">
          <P>The Viysr platform, including its design, code, content, and brand, is owned by Viysr, Inc. and protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.</P>
          <P>By submitting content to the platform (including profile information and communications), you grant Viysr a non-exclusive, royalty-free license to use that content to operate and improve the platform.</P>
        </Section>

        <Section title="8. Disclaimers">
          <P>The platform is provided "as is" without warranties of any kind, express or implied. Viysr does not warrant that the platform will be uninterrupted, error-free, or free of harmful components. Viysr makes no representations regarding the qualifications or suitability of any advisor listed on the platform.</P>
        </Section>

        <Section title="9. Limitation of Liability">
          <P>To the maximum extent permitted by law, Viysr shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform, even if we have been advised of the possibility of such damages. Our total liability to you shall not exceed the greater of $100 or the amount you paid to Viysr in the 12 months preceding the claim.</P>
        </Section>

        <Section title="10. Indemnification">
          <P>You agree to indemnify and hold harmless Viysr and its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the platform, your violation of these Terms, or your violation of any third-party rights.</P>
        </Section>

        <Section title="11. Termination">
          <P>You may terminate your account at any time through your account settings. Viysr may suspend or terminate your account at any time for violation of these Terms or for any other reason with or without notice.</P>
        </Section>

        <Section title="12. Governing Law">
          <P>These Terms are governed by the laws of the State of California, without regard to conflict of law principles. Any disputes shall be resolved in the state or federal courts located in San Diego County, California.</P>
        </Section>

        <Section title="13. Changes to Terms">
          <P>We may update these Terms at any time. Material changes will be communicated via email or prominent notice on the platform. Your continued use after changes constitutes acceptance.</P>
        </Section>

        <Section title="14. Contact">
          <P>Questions about these Terms? Contact us at <strong>legal@viysr.com</strong>.</P>
        </Section>
      </LegalShell>
    </PageWrapper>
  );
}

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const subjects = ["General question", "Business owner support", "Advisor support", "Billing issue", "Report a problem", "Press / media", "Other"];
  const valid = form.name.trim() && form.email.includes("@") && form.subject && form.message.trim().length > 10;

  return (
    <PageWrapper>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 48px 100px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
        <div>
          <p style={{ fontSize: 11, fontFamily: "sans-serif", letterSpacing: "0.16em", color: G, fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>◆ CONTACT</p>
          <h1 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 400, color: "#0a1a0f", margin: "0 0 20px", fontFamily: "Georgia, serif", lineHeight: 1.1 }}>
            We're here<br /><em style={{ color: G }}>to help.</em>
          </h1>
          <p style={{ fontSize: 15, color: "#888", fontFamily: "sans-serif", lineHeight: 1.7, margin: "0 0 40px" }}>
            Have a question about Viysr? Need help with your account? Want to report an issue? Reach out and we'll get back to you within one business day.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {[
              { label: "General & Support", value: "hello@viysr.com" },
              { label: "Legal & Privacy", value: "legal@viysr.com" },
              { label: "Press & Media", value: "press@viysr.com" },
            ].map(item => (
              <div key={item.label}>
                <div style={{ fontSize: 11, fontFamily: "sans-serif", fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 15, fontFamily: "sans-serif", color: G, fontWeight: 600 }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 20, padding: "36px 32px", border: `1.5px solid ${BORDER}` }}>
          {sent ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ width: 56, height: 56, background: MINT, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 24 }}>✓</div>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: "#0a1a0f", fontFamily: "Georgia, serif", marginBottom: 8 }}>Message sent</h3>
              <p style={{ fontSize: 14, color: "#aaa", fontFamily: "sans-serif" }}>We'll get back to you within one business day.</p>
              <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                style={{ marginTop: 24, background: "none", border: "none", fontSize: 13, color: G, fontFamily: "sans-serif", cursor: "pointer", textDecoration: "underline" }}>
                Send another message
              </button>
            </div>
          ) : (
            <>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: "#0a1a0f", fontFamily: "Georgia, serif", margin: "0 0 24px" }}>Send us a message</h2>
              {[["Full name", "name", "text", "Your name"], ["Email address", "email", "email", "you@company.com"]].map(([label, key, type, ph]) => (
                <div key={key} style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontFamily: "sans-serif", fontWeight: 700, color: "#666", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</label>
                  <input type={type} placeholder={ph} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    style={{ width: "100%", padding: "11px 13px", border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "sans-serif", outline: "none", boxSizing: "border-box" }}
                    onFocus={e => e.target.style.borderColor = G} onBlur={e => e.target.style.borderColor = BORDER} />
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontFamily: "sans-serif", fontWeight: 700, color: "#666", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>Subject</label>
                <select value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  style={{ width: "100%", padding: "11px 13px", border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "sans-serif", outline: "none", boxSizing: "border-box", background: "#fff", color: form.subject ? "#0a1a0f" : "#bbb" }}
                  onFocus={e => e.target.style.borderColor = G} onBlur={e => e.target.style.borderColor = BORDER}>
                  <option value="">Select a subject...</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, fontFamily: "sans-serif", fontWeight: 700, color: "#666", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>Message</label>
                <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us how we can help..."
                  rows={5} style={{ width: "100%", padding: "11px 13px", border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "sans-serif", outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }}
                  onFocus={e => e.target.style.borderColor = G} onBlur={e => e.target.style.borderColor = BORDER} />
              </div>
              <button onClick={() => valid && setSent(true)}
                style={{ width: "100%", background: valid ? G : "#e8e5e0", color: valid ? "#fff" : "#bbb", border: "none", padding: "13px", borderRadius: 999, fontSize: 15, fontFamily: "sans-serif", fontWeight: 700, cursor: valid ? "pointer" : "default", transition: "all 0.2s ease" }}>
                Send Message →
              </button>
            </>
          )}
        </div>
      </div>
    </PageWrapper>
  );
}
